import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageCircle, GitBranch, CalendarClock, ThumbsUp, UserPlus, Radio, Mail, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/context/AppContext";
import { useRealtime, fmt } from "@/context/RealtimeContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApi } from "@/lib/useApi";

const ICONS = { like: [Heart, "text-rose-300"], message: [MessageCircle, "text-cyan-300"], state: [GitBranch, "text-emerald-300"], interview: [CalendarClock, "text-yellow-300"], intent_like: [ThumbsUp, "text-rose-300"], follow: [UserPlus, "text-cyan-300"], broadcast: [Radio, "text-rose-300"], reminder: [CalendarClock, "text-yellow-300"], feedback: [Star, "text-yellow-300"], feedback_request: [Star, "text-cyan-300"] };

const EmailToggle = () => {
  const { t, lang } = useLang();
  const { data: social, setData: setSocial } = useApi("/me/social");
  const on = social ? social.email_alerts : null;
  const save = async (v) => { setSocial((s) => ({ ...s, email_alerts: v })); await api.patch("/me/preferences", { email_alerts: v, lang }); };
  if (on === null) return null;
  return (
    <label className="flex items-center gap-3 border-t border-white/5 px-4 py-3 text-xs text-slate-400">
      <Mail className="h-4 w-4 text-emerald-300" /><span className="flex-1">{t("email_alerts")}</span>
      <Switch checked={on} onCheckedChange={save} data-testid="email-alerts-toggle" />
    </label>
  );
};

export const NotificationBell = () => {
  const { t } = useLang();
  const { items, unread, connected, markAll, markOne } = useRealtime();
  const nav = useNavigate();
  const open = (n) => { if (!n.read) markOne(n.id); if (n.link) nav(n.link); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative text-slate-300 transition-colors hover:text-white" data-testid="notification-bell" title={t("notifications")}>
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 font-mono text-[10px] font-bold text-white" data-testid="notification-unread-count">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 border-white/10 bg-[#111827] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
          <p className="flex items-center gap-2 font-display font-bold text-white">{t("notifications")}
            <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-600"}`} data-testid="realtime-status" title={t("live")} /></p>
          {unread > 0 && <button onClick={markAll} className="text-xs text-emerald-300 hover:underline" data-testid="notification-mark-all">{t("mark_all_read")}</button>}
        </div>
        <ul className="max-h-96 overflow-y-auto">
          {!items.length && <li className="p-4 text-sm text-slate-500">{t("no_notifications")}</li>}
          {items.map((n) => {
            const [Icon, tone] = ICONS[n.type] || [Bell, "text-slate-300"];
            return (
              <li key={n.id}>
                <button onClick={() => open(n)} data-testid={`notification-item-${n.id}`} className={`flex w-full gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/5 ${n.read ? "opacity-60" : ""}`}>
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-200">{fmt(t, n)}</span>
                    <span className="font-mono text-[10px] text-slate-500">{new Date(n.created_at).toLocaleString()}</span>
                  </span>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-400" />}
                </button>
              </li>
            );
          })}
        </ul>
        <EmailToggle />
      </PopoverContent>
    </Popover>
  );
};
